import {
  type BuyerTeamMember,
  type BuyerJoinRequest,
} from '@choco-admin/types';
import { getInitials } from '@choco-admin/utils';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Chip,
  Spinner,
} from '@heroui/react';
import React, { useState, useCallback } from 'react';
import { FiPlus, FiUsers, FiMail, FiPhone } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../../contexts/NotificationContext';
import {
  useBuyerRemoveUserMutationMutation,
  type Buyer,
} from '../../../../generated';
import { useUpdateJoinRequest } from '../../hooks/useUpdateJoinRequest';

import { CreateTeamMemberModal } from './CreateTeamMemberModal';
import { useBuyerTeam } from './hooks/useBuyerTeam';
import { RemoveTeamMemberModal } from './RemoveTeamMemberModal';

interface TeamTabProps {
  buyer: Pick<Buyer, 'id'>;
}

export const TeamTab: React.FC<TeamTabProps> = ({ buyer }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [userToRemove, setUserToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const {
    teamMembers,
    requests,
    rolesEnabled,
    isLoading,
    teamError,
    requestsError,
    refetchAll,
  } = useBuyerTeam(buyer.id);

  const [removeUser] = useBuyerRemoveUserMutationMutation();

  const { updateJoinRequest, processingRequestId } = useUpdateJoinRequest(
    buyer.id
  );

  const getRoleColor = useCallback(
    (role?: string | null): 'danger' | 'primary' | 'default' => {
      switch (role?.toUpperCase()) {
        case 'ADMIN':
          return 'danger';
        case 'MEMBER':
          return 'primary';
        case 'VIEWER':
          return 'default';
        default:
          return 'default';
      }
    },
    []
  );

  const handleAddSuccess = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  const handleRemoveUser = useCallback((userId: string, userName: string) => {
    setUserToRemove({ id: userId, name: userName });
    setIsRemoveModalOpen(true);
  }, []);

  const handleTeamMemberClick = useCallback(
    (userId: string) => {
      navigate(`/users/${userId}`);
    },
    [navigate]
  );

  const handleTeamMemberKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>, userId: string) => {
      if (
        event.target === event.currentTarget &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        handleTeamMemberClick(userId);
      }
    },
    [handleTeamMemberClick]
  );

  const handleCloseRemoveModal = useCallback(() => {
    setIsRemoveModalOpen(false);
    setUserToRemove(null);
    setRemovingUserId(null);
  }, []);

  const confirmRemoveUser = useCallback(async () => {
    if (!userToRemove) return;

    setRemovingUserId(userToRemove.id);
    try {
      await removeUser({
        variables: {
          buyerId: buyer.id,
          userId: userToRemove.id,
        },
      });

      addNotification({
        type: 'success',
        title: 'Team Member Removed',
        message: `${userToRemove.name} has been successfully removed from the team.`,
        duration: 5000,
      });

      refetchAll();
      handleCloseRemoveModal();
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Failed to Remove Team Member',
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred while removing the team member. Please try again.',
        duration: 7000,
      });
    } finally {
      setRemovingUserId(null);
    }
  }, [
    userToRemove,
    buyer.id,
    removeUser,
    addNotification,
    refetchAll,
    handleCloseRemoveModal,
  ]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Requests to Join Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <h3 className="text-lg font-semibold">Requests to Join</h3>
          {requests.length > 0 && (
            <Chip size="sm" variant="flat" color="warning">
              {requests.length} pending
            </Chip>
          )}
        </CardHeader>
        <CardBody>
          {requestsError ? (
            <div className="text-center py-8 text-danger">
              <p>
                Error loading requests:{' '}
                {requestsError instanceof Error
                  ? requestsError.message
                  : 'Unknown error occurred'}
              </p>
            </div>
          ) : (
            <>
              {requests.length === 0 ? (
                <div className="text-center py-8 text-default-500">
                  <FiUsers size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No pending requests to join this buyer.</p>
                </div>
              ) : (
                <Table aria-label="Requests to join table">
                  <TableHeader>
                    <TableColumn>User</TableColumn>
                    <TableColumn>Contact</TableColumn>
                    <TableColumn>Actions</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request: BuyerJoinRequest) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar
                              name={getInitials(request.user?.name || 'U')}
                              size="sm"
                              color="warning"
                            />
                            <div>
                              <p className="font-semibold">
                                {request.user?.name || 'Unknown User'}
                              </p>
                              <p className="text-sm text-default-500">
                                Requested to join
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {request.user?.email && (
                              <div className="flex items-center gap-1">
                                <FiMail size={12} />
                                {request.user.email}
                              </div>
                            )}
                            {request.user?.phone && (
                              <div className="flex items-center gap-1 mt-1">
                                <FiPhone size={12} />
                                {request.user.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              color="success"
                              variant="flat"
                              isLoading={processingRequestId === request.id}
                              isDisabled={processingRequestId !== null}
                              onPress={() =>
                                updateJoinRequest(request.id, 'approved')
                              }
                            >
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              color="danger"
                              variant="flat"
                              isLoading={processingRequestId === request.id}
                              isDisabled={processingRequestId !== null}
                              onPress={() =>
                                updateJoinRequest(request.id, 'denied')
                              }
                            >
                              Decline
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Team Members Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">Team Members</h3>
            {teamMembers.length > 0 && (
              <Chip size="sm" variant="flat" color="primary">
                {teamMembers.length} members
              </Chip>
            )}
          </div>
          <Button
            color="primary"
            startContent={<FiPlus size={16} />}
            size="sm"
            onPress={() => setIsAddModalOpen(true)}
          >
            Add Team Member
          </Button>
        </CardHeader>
        <CardBody>
          {teamError ? (
            <div className="text-center py-8 text-danger">
              <p>
                Error loading team members:{' '}
                {teamError instanceof Error
                  ? teamError.message
                  : 'Unknown error occurred'}
              </p>
            </div>
          ) : (
            <>
              {teamMembers.length === 0 ? (
                <div className="text-center py-8 text-default-500">
                  <FiUsers size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No team members found for this buyer.</p>
                  <p className="text-sm mt-2">
                    Add team members to manage access and permissions.
                  </p>
                </div>
              ) : (
                <Table aria-label="Team members table">
                  <TableHeader>
                    <TableColumn>Member</TableColumn>
                    <TableColumn>Contact</TableColumn>
                    <TableColumn className={rolesEnabled ? '' : 'hidden'}>
                      Role
                    </TableColumn>
                    <TableColumn>Actions</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {teamMembers.map((member: BuyerTeamMember) => (
                      <TableRow
                        key={member.id}
                        className="group cursor-pointer transition-colors hover:bg-default-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                        tabIndex={0}
                        aria-label={`View ${member.name || 'team member'} user profile`}
                        onClick={() => handleTeamMemberClick(member.id)}
                        onKeyDown={(event) =>
                          handleTeamMemberKeyDown(event, member.id)
                        }
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar
                              src={member.profileImage?.url || undefined}
                              name={getInitials(member.name || 'U')}
                              size="md"
                              color="primary"
                            />
                            <div>
                              <p className="font-semibold transition-colors group-hover:text-primary">
                                {member.name || 'Unknown Member'}
                              </p>
                              <p className="text-sm text-default-500">
                                Team Member
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {member.email && (
                              <div className="flex items-center gap-1">
                                <FiMail size={12} />
                                {member.email}
                              </div>
                            )}
                            {member.phone && (
                              <div className="flex items-center gap-1 mt-1">
                                <FiPhone size={12} />
                                {member.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={rolesEnabled ? '' : 'hidden'}>
                          <Chip
                            size="sm"
                            variant="flat"
                            color={getRoleColor(member.role)}
                          >
                            {member.role || 'Member'}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="light"
                            color="danger"
                            onClick={(event) => event.stopPropagation()}
                            onPress={() =>
                              handleRemoveUser(
                                member.id,
                                member.name || 'Unknown Member'
                              )
                            }
                            isLoading={removingUserId === member.id}
                            isDisabled={removingUserId !== null}
                          >
                            {removingUserId === member.id
                              ? 'Removing...'
                              : 'Remove'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {rolesEnabled && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
            </div>
            <div className="text-sm">
              <p className="font-medium text-primary-700 dark:text-primary-300 mb-1">
                Roles & Permissions Enabled
              </p>
              <p className="text-primary-600 dark:text-primary-400">
                Team members can be assigned specific roles with different
                permission levels. Make sure at least one team member has Admin
                access.
              </p>
            </div>
          </div>
        </div>
      )}

      <CreateTeamMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        buyerId={buyer.id}
        onSuccess={handleAddSuccess}
      />

      <RemoveTeamMemberModal
        isOpen={isRemoveModalOpen}
        onClose={handleCloseRemoveModal}
        onConfirm={confirmRemoveUser}
        memberName={userToRemove?.name || ''}
        isRemoving={removingUserId !== null}
      />
    </div>
  );
};
